import React from 'react'
import { compose } from 'redux'
import { Field, propTypes as formPropTypes, change } from 'redux-form'
import { lpForm } from 'lp-form'
import { push } from 'react-router-redux';
import { Terms, RecaptchaInput } from 'components'
import { modifyProps } from 'lp-utils'
import { Input, SubmitButton, Checkbox, Spinner, Select } from 'lp-components'
import * as actions from '../actions'
import PropTypes from 'prop-types'
import {
  CENTAMAN_ERROR,
  VALIDATION_ERROR,
  scrollToTop,
  set,
  trackPurchaseFrom
} from 'utils'
import * as LS from 'local-storage'
import { get } from 'lodash'
import { GpiForm, GpiSubmit } from "components/Gpi";
import { COUNTRY_OPTIONS } from "utils/countryOptions";

const propTypes = {
  hidePhoneNumber: PropTypes.bool,
  orderInfo: PropTypes.object,
  setBillingError: PropTypes.func,
  tickets: PropTypes.array,
  ticketType: PropTypes.string,
  showTermsModal: PropTypes.func,
  skipPaymentDetails: PropTypes.bool,
  ...formPropTypes,
}

const defaultProps = {
  hidePhoneNumber: false
}

const validateRecaptcha = (value) => {
  // Value will subsequently be validated server-side during order submission
  if (!value) return 'reCAPTCHA must be verified'
}

function BillingForm(
    {
      handleSubmit,
      hidePhoneNumber,
      submitting,
      skipPaymentDetails,
      orderInfo,
      change
    }) {
  return (
      <div className="billing-form-wrapper">
        {submitting && (
          <div className="billing-form-loading">
            <Spinner />
            <p>Processing payment...</p>
          </div>
        )}
        <form
          className={submitting ? 'form-submitting' : ''}
          onSubmit={(e, vals) => {
          e.preventDefault()
          change('donationAmount', orderInfo.donationAmount)
          handleSubmit(vals)
        }}>
          <div className="cc-form">
            <h4 className="billing-header">{skipPaymentDetails ? 'Contact Details' : 'Billing Details'}</h4>
            <div className="billing-details">
              <div className="billing-form">
                <Field
                    name="firstName" component={Input}
                    className="input-short input-short"
                    label={skipPaymentDetails ? "First Name" : "First Name (as shown on card) *"}
                />
                <Field
                    name="lastName" component={Input}
                    className="input-short input-short-right"
                    label={skipPaymentDetails ? "Last Name" : "Last Name (as shown on card) *"}
                />
                <p className="required-field" style={{ fontSize: '14px', textAlign: 'center' }}>
                  Please use billing address on file with your card issuer
                </p>
                <Field
                    name="streetAddress1"
                    component={Input}
                    label="Street Address"
                    required
                    requiredIndicator="*"
                />
                <Field
                    name="streetAddress2"
                    component={Input}
                    label="Apartment, suite, etc. (optional)"
                />
                <Field
                    name="city"
                    component={Input}
                    className="input-short input-short"
                    label="City"
                    required
                    requiredIndicator="*"
                />
                <Field
                    name="state"
                    component={Input}
                    className="input-short input-short-right"
                    label="State/Province"
                    required
                    requiredIndicator="*"
                />
                <Field
                    name="zip"
                    component={Input}
                    className="input-short input-short"
                    label="Postal Code"
                    required
                    requiredIndicator="*"
                />
                <Field
                    name="country"
                    component={Select}
                    className="input-short input-short-right"
                    label="Country"
                    required
                    requiredIndicator="*"
                    options={COUNTRY_OPTIONS}
                    enablePlaceholderOption
                    placeholder="Select Country"
                />
                {!skipPaymentDetails && <GpiForm />}
                <Field
                    name="email"
                    component={Input}
                    label="Email (confirmation will be sent to this address) *"
                />
                {!hidePhoneNumber &&
                    <Field
                        name="phone"
                        component={Input}
                        label="Phone *"
                    />
                }
                <label style={{marginTop: 25 + 'px'}}>* denotes a required field</label>
              </div>
            </div>
            <div className="checkbox terms">
              <Field
                  name="termsAgreed"
                  component={Checkbox}
                  labelComponent={Terms}
              />
            </div>
            <Field
                name="recaptcha"
                label={false}
                component={RecaptchaInput}
                recaptchaSiteKey={process.env.GOOGLE_RECAPTCHA_CLIENT_KEY}
                validate={validateRecaptcha}
            />
            <hr/>
            {skipPaymentDetails ? (
              <SubmitButton
                  submitting={submitting}
                  className="button-primary">
                Submit Order
              </SubmitButton>
            ) : (
              <GpiSubmit />
            )}
          </div>
        </form>

      </div>
  )
}

BillingForm.propTypes = propTypes
BillingForm.defaultProps = defaultProps

const orderSuccess = ({response, dispatch, props: {orderInfo, tickets, ticketType}}) => {
  const {token: orderToken, receipt, coupon} = response.data.attributes

  // Membership Order
  if (response && get(response, 'data.type') === 'membership_order') {
    LS.clearMembershipOrderDetails()
    return dispatch(push(`/memberships/confirmation/${orderToken}`))
  }

  // Ticketing Order
  sendTransactionToGoogle({transactionId: receipt, orderInfo, tickets, ticketType, coupon})
  dispatch(push(`/confirmation/${orderToken}`))
  return dispatch(actions.clearOrderInfo())
}

const orderError = ({dispatch, submitError, props: {setBillingError}}) => {
  handleOrderError(submitError, dispatch, setBillingError)
}

function handleOrderError(error, dispatch, setBillingError) {
  scrollToTop()
  dispatch(change('coupon-form', 'coupon', null))
  if (error && error.errors && error.errors.title) return setBillingError(error.errors)
  if (error && error.gpi) return setBillingError(error)
  if (!error) return setBillingError(VALIDATION_ERROR)
  setBillingError(CENTAMAN_ERROR)
}

function sendTransactionToGoogle({transactionId, orderInfo, tickets, ticketType, coupon}) {
  const total = orderInfo.orderTotal || orderInfo.total
  trackPurchaseFrom(transactionId, total, coupon, ticketType, tickets)
}

function modify({initialValues}) {
  return {
    initialValues: set('termsAgreed', false, initialValues || {}),
    onSubmitSuccess: (response, dispatch, props) => orderSuccess({response, dispatch, props}),
    onSubmitFail: (error, dispatch, submitError, props) => orderError({error, dispatch, submitError, props}),
  }
}

export default compose(
    modifyProps(modify),
    lpForm({
      name: 'billing-form',
      constraints: {
        'firstName': {
          presence: true,
          length: {maximum: 29}
        },
        'lastName': {
          presence: true,
          length: {maximum: 30}
        },
        'email': {
          presence: true,
          email: true,
          length: {maximum: 60}
        },
        'phone': {
          presence: true,
          length: {maximum: 20}
        },
        'streetAddress1': {
          presence: true,
          length: {maximum: 60}
        },
        'streetAddress2': {
          length: {maximum: 60}
        },
        'city': {
          presence: true,
          length: {maximum: 50}
        },
        'state': {
          presence: true,
          length: {maximum: 20}
        },
        'zip': {
          presence: true,
          length: {maximum: 15}
        },
        'country': {
          presence: true,
          length: {maximum: 60}
        },
        'termsAgreed': {
          presence: {
            message: '^You must accept the terms and conditions',
          },
          inclusion: {
            within: [true],
            message: '^You must accept the terms and conditions',
          },
        }
      }
    }),
)(BillingForm)
